import * as THREE from 'three';
import type { GameContext } from '../core/context';
import type { V2 } from '../core/math';
import { Rng } from '../core/rng';
import type { System } from '../core/system';
import { Layer } from '../physics/types';
import { weaponFromWire } from '../../shared/weapons';
import { pelletHeadings, traceShot } from './combat';
import { keyLabel } from '../ui/format';
import { Joint, noteAttention, noteHit, type CharacterRig } from '../render/characters';
import type { Pellet, WeaponService } from './types';
import { WEAPONS, type WeaponId } from './weaponData';
import { WeaponHud } from './weaponHud';
import { acquireWeaponMaterials, buildAmmoBox, buildWeaponMesh, casingGeometry, disposeWeaponMesh, muzzleFlashGeometry, releaseWeaponMaterials, tracerGeometry } from './weaponMeshes';

/**
 * Weapons presentation (order 713, after the player view has posed the rig this frame): the weapon
 * in the player's hand, muzzle flash (mesh + light), tracers, spent cases dropped on a reload, street
 * pick-ups, armed officers'
 * revolvers with their aim line (the telegraph), the HUD, and the radio / shouted warnings. Online it
 * also puts other players' weapons in their figures' hands (`remoteRigs`) and draws their shots
 * (`netShot`, traced against this client's city at the shooter's render time, then announced as
 * `netShotTraced`). Impact marks, surface sparks / dust and impact sounds are the impact kit's
 * (src/fx/impactFx.ts). Reads the `weapons`, `police` and `net` services and weapon events; never
 * touches simulation state.
 */

const TRACER_POOL = 14;
const TRACER_LIFE = 0.07;
const CASING_POOL = 24;
const COP_GUNS = 4;
const FLASH_LIFE = 0.055;
/** Street pick-ups are drawn larger again than weapons in hand, so the profile inside the glint ring reads from the follow camera. */
const PICKUP_SCALE = 1.3;
/** Look-development row (`weaponLineup`): every weapon as a ground pick-up, in roster order. */
const LINEUP: WeaponId[] = ['revolver', 'sawnoff', 'cosh', 'bat', 'crowbar', 'knife'];
/** Shots are drawn from the gun at chest height to here (m). */
const SHOT_Y = 1.25;

interface Tracer {
  mesh: THREE.Mesh;
  life: number;
}

interface Casing {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  spin: number;
  rot: number;
  age: number;
  shell: boolean;
}

interface PendingShot {
  kind: 'player' | 'police' | 'remote';
  from: V2;
  pellets: Pellet[];
  weapon: WeaponId;
  /** Remote shots: the shooter's gun mesh (its muzzle), when drawn. */
  gun?: THREE.Group | null;
}

/** Another player's weapon in their figure's hand. */
interface RemoteGun {
  rig: CharacterRig;
  weapon: WeaponId;
  mesh: THREE.Group | null;
}

declare module '../core/hooks' {
  interface BhHooks {
    /**
     * Look-development aid (docs/WEAPONS.md, "Weapon models"): lay every weapon on the ground as a
     * pick-up in a row along +X from (x, z), still (no spin). Returns how many; `weaponLineup(null)` clears.
     */
    weaponLineup(x: number | null, z?: number): number;
  }
}

export function createWeaponViewSystem(): System {
  let ctx: GameContext;
  let weapons: WeaponService | undefined;
  let rig: CharacterRig | undefined;
  const root = new THREE.Group();
  root.name = 'weapons-fx';
  const inHand = new Map<WeaponId, THREE.Group>();
  let shownInHand: WeaponId | null = null;
  const offs: (() => void)[] = [];
  const pending: PendingShot[] = [];
  const reloadDrops: { weapon: WeaponId; pos: V2 }[] = [];
  let hud: WeaponHud | null = null;
  let warnUntil = 0;
  let shoutAt = -99;
  const remoteGuns = new Map<number, RemoteGun>();
  const remoteShots: { eid: number; weapon: WeaponId; from: V2; heading: number; at: number; seed: number }[] = [];

  // Muzzle flash: a star burst across the bore and flame tongues down the line of fire (one additive
  // vertex-coloured mesh, weaponMeshes.muzzleFlashGeometry), turned to the shot and rolled per shot.
  const flashMat = new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 1, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide });
  const flashGeo = muzzleFlashGeometry();
  const flash = new THREE.Group();
  const flashMesh = new THREE.Mesh(flashGeo, flashMat);
  flashMesh.frustumCulled = false;
  flash.add(flashMesh);
  flash.visible = false;
  let flashLife = 0;
  root.add(flash);

  // Tracers: crossed ribbons stretched between muzzle and impact, bright at the far end.
  const tracerGeo = tracerGeometry();
  const tracerMat = new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.9, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide });
  const tracers: Tracer[] = [];
  for (let i = 0; i < TRACER_POOL; i++) {
    const mesh = new THREE.Mesh(tracerGeo, tracerMat);
    mesh.visible = false;
    root.add(mesh);
    tracers.push({ mesh, life: 0 });
  }
  let nextTracer = 0;

  const tmpM = new THREE.Matrix4();
  const tmpQ = new THREE.Quaternion();
  const tmpE = new THREE.Euler();
  const tmpS = new THREE.Vector3();
  const tmpP = new THREE.Vector3();
  const tmpV = new THREE.Vector3();
  /** The player's last blow or round that landed, for the HUD's hit marker (taken once, by markHit). */
  let hitMark: { x: number; z: number; kill: boolean } | null = null;

  function markHit(): { x: number; y: number; kill: boolean } | null {
    const m = hitMark;
    hitMark = null;
    if (!m || !ctx.render) return null;
    const v = tmpV.set(m.x, 1.1, m.z).project(ctx.camera);
    return { x: ((v.x + 1) / 2) * ctx.render.canvas.clientWidth, y: ((1 - v.y) / 2) * ctx.render.canvas.clientHeight, kill: m.kill };
  }
  const tmpBox = new THREE.Box3();
  const down = new THREE.Vector3(0, -1, 0);

  // Spent cases (revolver) and cartridges (sawn-off), dropped on a reload.
  let mats: Record<string, THREE.MeshStandardMaterial> | null = null;
  let brass: THREE.InstancedMesh | null = null;
  let shells: THREE.InstancedMesh | null = null;
  const casings: Casing[] = [];

  // Street pick-ups and dropped hauls.
  const pickupMeshes = new Map<number, THREE.Group>();
  const lineup: THREE.Group[] = [];
  const glintMat = new THREE.MeshBasicMaterial({ color: 0xdba73f, transparent: true, opacity: 0.6, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide });
  const glintGeo = new THREE.RingGeometry(0.42, 0.52, 20).rotateX(-Math.PI / 2);

  // Armed officers' revolvers and aim lines.
  const copGuns: THREE.Group[] = [];
  const aimLines: THREE.Line[] = [];
  const aimMat = new THREE.LineDashedMaterial({ color: 0xd03420, dashSize: 0.45, gapSize: 0.3, transparent: true, opacity: 0.6, depthWrite: false });

  function ensureHand(id: WeaponId): void {
    if (!rig) return;
    // The detailed figure hangs the gun on its own hand bone (rig.handRSocket); follow a switch.
    const hand = rig.handRSocket ?? rig.joints[Joint.handR]!;
    if (shownInHand && shownInHand !== 'fists') {
      const cur = inHand.get(shownInHand);
      if (cur && cur.parent !== hand) hand.add(cur);
    }
    if (shownInHand === id) return;
    if (shownInHand) {
      const old = inHand.get(shownInHand);
      if (old) old.visible = false;
    }
    shownInHand = id;
    if (id === 'fists') return;
    let g = inHand.get(id);
    if (!g) {
      g = buildWeaponMesh(id);
      inHand.set(id, g);
      hand.add(g);
      // Sit the grip in the fist (the hand mesh hangs below the wrist joint).
      g.position.set(0, -0.045, 0.01);
    }
    g.visible = true;
  }

  function muzzleWorld(out: THREE.Vector3, fallback: V2): THREE.Vector3 {
    const g = shownInHand ? inHand.get(shownInHand) : undefined;
    const m = g?.getObjectByName('muzzle');
    if (m && g!.visible && rig?.root.visible) return m.getWorldPosition(out);
    return out.set(fallback.x, SHOT_Y + 0.15, fallback.z);
  }

  function tracer(a: THREE.Vector3, b: THREE.Vector3): void {
    const t = tracers[nextTracer]!;
    nextTracer = (nextTracer + 1) % TRACER_POOL;
    const len = a.distanceTo(b);
    if (len < 0.05) return;
    t.mesh.position.copy(a);
    t.mesh.lookAt(b);
    t.mesh.scale.set(1, 1, len);
    t.mesh.visible = true;
    t.life = TRACER_LIFE;
  }

  function dropCasings(weapon: WeaponId, pos: V2): void {
    const shell = WEAPONS[weapon].cls === 'shotgun';
    const n = WEAPONS[weapon].firearm?.capacity ?? 0;
    for (let i = 0; i < n; i++) {
      if (casings.length >= CASING_POOL) casings.shift();
      const a = Math.random() * Math.PI * 2;
      casings.push({ x: pos.x + Math.cos(a) * 0.15, y: 1.0, z: pos.z + Math.sin(a) * 0.15, vx: Math.cos(a) * 0.8, vy: 0.4 + Math.random(), vz: Math.sin(a) * 0.8, spin: (Math.random() - 0.5) * 20, rot: Math.random() * 6, age: 0, shell });
    }
  }

  function drawShot(s: PendingShot): void {
    const particles = ctx.services.tryGet('particles');
    const remoteMuzzle = s.kind === 'remote' && s.gun?.visible ? s.gun.getObjectByName('muzzle') : undefined;
    const from =
      s.kind === 'player' ? muzzleWorld(tmpV, s.from) : remoteMuzzle ? remoteMuzzle.getWorldPosition(tmpV) : tmpV.set(s.from.x, SHOT_Y + (s.kind === 'remote' ? 0.15 : 0.2), s.from.z);
    const muzzle = from.clone();
    flash.position.copy(muzzle);
    // Down the line of fire (towards the first round's end), rolled at random so no two look alike.
    const aim = s.pellets[0]?.to;
    const dx = aim ? aim.x - muzzle.x : 0;
    const dz = aim ? aim.z - muzzle.z : 0;
    const dl = Math.hypot(dx, dz);
    tmpP.set(muzzle.x + (dl > 1e-3 ? dx / dl : 0), muzzle.y, muzzle.z + (dl > 1e-3 ? dz / dl : 1));
    flash.lookAt(tmpP);
    flash.rotateZ(Math.random() * Math.PI * 2);
    flash.scale.setScalar((WEAPONS[s.weapon].cls === 'shotgun' ? 1.5 : 1) * (0.85 + Math.random() * 0.3));
    flash.visible = true;
    flashLife = FLASH_LIFE;
    particles?.flash({ x: muzzle.x, y: muzzle.y, z: muzzle.z }, WEAPONS[s.weapon].cls === 'shotgun' ? 2.2 : 1.4, 0.07, 0xffc27a);
    particles?.emit('muzzle', { x: muzzle.x, y: muzzle.y, z: muzzle.z }, { dir: { x: dl > 1e-3 ? dx / dl : 0, z: dl > 1e-3 ? dz / dl : 1 }, intensity: WEAPONS[s.weapon].cls === 'shotgun' ? 1.4 : 1 });
    particles?.emit('dust', { x: muzzle.x, y: muzzle.y, z: muzzle.z }, { count: 2, intensity: 0.35, color: 0x9a948a });
    const shotgun = s.pellets.length > 1;
    // Only some pellets get a visible streak; the marks, sparks and dust where they land are the impact kit's.
    s.pellets.forEach((p, i) => {
      if (!shotgun || i % 3 === 0) tracer(muzzle, tmpP.set(p.to.x, SHOT_Y, p.to.z));
    });
  }

  function buildPickup(weapon: WeaponId, dropped: boolean): THREE.Group {
    const g = new THREE.Group();
    const w = buildWeaponMesh(weapon);
    // Lie it down the way it would fall, showing its profile to the camera above: guns, the cosh and
    // the crowbar on their left side (the hand's +X up), the bat and the knife on their flat face (+Z
    // up). Centred on the pick-up so it turns on the spot, resting on the setts.
    if (weapon === 'bat' || weapon === 'knife') w.rotation.set(-Math.PI / 2, 0, 0);
    else w.rotation.set(0, 0, Math.PI / 2);
    w.scale.multiplyScalar(PICKUP_SCALE);
    w.updateWorldMatrix(false, true);
    const bb = tmpBox.setFromObject(w);
    w.position.set(-(bb.min.x + bb.max.x) / 2, -bb.min.y + 0.004, -(bb.min.z + bb.max.z) / 2);
    g.add(w);
    if (dropped) {
      const box = buildAmmoBox();
      box.position.set(0.25, 0.04, 0.12);
      g.add(box);
    }
    const ring = new THREE.Mesh(glintGeo, glintMat);
    ring.position.y = 0.02;
    ring.name = 'glint';
    g.add(ring);
    root.add(g);
    return g;
  }

  function syncPickups(time: number): void {
    if (!weapons) return;
    const seen = new Set<number>();
    for (const k of weapons.pickups()) {
      seen.add(k.id);
      let g = pickupMeshes.get(k.id);
      if (!g) {
        g = buildPickup(k.weapon, k.kind === 'dropped');
        pickupMeshes.set(k.id, g);
      }
      g.visible = k.available;
      g.position.set(k.pos.x, 0, k.pos.z);
      g.rotation.y = time * 0.6 + k.id;
      const ring = g.getObjectByName('glint');
      if (ring) ring.scale.setScalar(1 + 0.12 * Math.sin(time * 3 + k.id));
    }
    for (const [id, g] of pickupMeshes) {
      if (seen.has(id)) continue;
      g.getObjectByName('glint')?.removeFromParent(); // shared geometry
      disposeWeaponMesh(g);
      pickupMeshes.delete(id);
    }
  }

  /** Other players' weapons: a mesh in each remote figure's right hand, rebuilt when it changes. */
  function syncRemoteGuns(): void {
    const net = ctx.services.tryGet('net');
    const rigs = ctx.services.tryGet('remoteRigs');
    const seen = new Set<number>();
    if (net?.online && rigs) {
      for (const r of net.remotePlayers()) {
        const rr = rigs.get(r.eid);
        if (!rr) continue;
        seen.add(r.eid);
        const w = weaponFromWire(r.weapon);
        let g = remoteGuns.get(r.eid);
        if (g && (g.rig !== rr || g.weapon !== w)) {
          dropRemoteGun(r.eid, g);
          g = undefined;
        }
        // The detailed figure's hand (rr.handRSocket) arrives once its model is loaded: follow it.
        const hand = rr.handRSocket ?? rr.joints[Joint.handR]!;
        if (g?.mesh && g.mesh.parent !== hand) hand.add(g.mesh);
        if (!g) {
          const mesh = w === 'fists' ? null : buildWeaponMesh(w);
          if (mesh) {
            hand.add(mesh);
            mesh.position.set(0, -0.045, 0.01);
          }
          remoteGuns.set(r.eid, { rig: rr, weapon: w, mesh });
        }
      }
    }
    for (const [eid, g] of remoteGuns) if (!seen.has(eid)) dropRemoteGun(eid, g);
  }

  function dropRemoteGun(eid: number, g: RemoteGun): void {
    if (g.mesh) {
      g.mesh.removeFromParent();
      disposeWeaponMesh(g.mesh);
    }
    remoteGuns.delete(eid);
  }

  /**
   * Other players' shots, played when the render clock reaches them (in step with the interpolated
   * figure): pellets spread from the shot's seed, traced against this client's city for the end points.
   */
  function playRemoteShots(): void {
    if (!remoteShots.length) return;
    const net = ctx.services.tryGet('net');
    const now = net?.online ? net.renderTime(1) : Infinity;
    for (let i = 0; i < remoteShots.length; i++) {
      const r = remoteShots[i]!;
      if (r.at > now && r.at - now < 1000) continue;
      remoteShots.splice(i--, 1);
      const fa = WEAPONS[r.weapon].firearm;
      if (!fa) continue;
      const rng = new Rng(r.seed);
      const pellets: Pellet[] = pelletHeadings(r.heading, fa, () => rng.next()).map((h) => {
        const tr = traceShot(ctx.physics, r.from, h, fa.range, [], Layer.VEHICLE);
        return { to: tr.point, hit: tr.hit, normal: tr.normal, label: tr.label, bodyId: tr.bodyId };
      });
      if (pending.length < 8) pending.push({ kind: 'remote', from: r.from, pellets, weapon: r.weapon, gun: remoteGuns.get(r.eid)?.mesh ?? null });
      ctx.events.emit('netShotTraced', { eid: r.eid, weapon: r.weapon, from: r.from, pellets });
    }
  }

  function updateCopGuns(): void {
    const police = ctx.services.tryGet('police');
    const player = ctx.services.tryGet('player');
    let n = 0;
    let anyAim = false;
    if (police && player && police.armedAuthorised) {
      for (const u of police.units()) {
        // Armed officers on foot, and the passenger of an armed car aiming out of the window (docs/POLICE-AI-POLISH.md).
        if (!u.armed || u.role === 'disabled' || u.aiming <= 0 || n >= COP_GUNS) continue;
        anyAim = true;
        let gun = copGuns[n];
        if (!gun) {
          gun = buildWeaponMesh('revolver');
          gun.scale.multiplyScalar(1.22);
          copGuns.push(gun);
          root.add(gun);
          const line = new THREE.Line(new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), new THREE.Vector3(0, 0, 1)]), aimMat);
          line.frustumCulled = false;
          aimLines.push(line);
          root.add(line);
        }
        // The line ends at whoever is being aimed at: us, or (online) the player the server's unit is on.
        const at = u.aimAt ?? player.pos;
        const dx = at.x - u.x;
        const dz = at.z - u.z;
        const l = Math.hypot(dx, dz) || 1;
        tmpV.set(dx / l, 0, dz / l);
        gun.position.set(u.x + tmpV.x * (u.kind === 'car' ? 1.1 : 0.68), u.kind === 'car' ? 1.25 : 1.5, u.z + tmpV.z * (u.kind === 'car' ? 1.1 : 0.68));
        gun.quaternion.setFromUnitVectors(down, tmpV);
        gun.visible = true;
        const line = aimLines[n]!;
        const pos = line.geometry.getAttribute('position') as THREE.BufferAttribute;
        pos.setXYZ(0, gun.position.x + tmpV.x * 0.25, 1.48, gun.position.z + tmpV.z * 0.25);
        pos.setXYZ(1, at.x, 1.15, at.z);
        pos.needsUpdate = true;
        line.computeLineDistances();
        line.visible = true;
        aimMat.opacity = 0.25 + 0.6 * u.aiming;
        n++;
      }
    }
    for (let i = n; i < copGuns.length; i++) {
      copGuns[i]!.visible = false;
      aimLines[i]!.visible = false;
    }
    if (anyAim) warnUntil = ctx.state.time + 0.4;
  }

  function updateCasings(dt: number): void {
    if (!brass || !shells) return;
    let nb = 0;
    let ns = 0;
    for (let i = casings.length - 1; i >= 0; i--) {
      const c = casings[i]!;
      c.age += dt;
      if (c.age > 40) {
        casings.splice(i, 1);
        continue;
      }
      if (c.y > 0.012 || c.vy > 0) {
        c.vy -= 9.8 * dt;
        c.x += c.vx * dt;
        c.z += c.vz * dt;
        c.y += c.vy * dt;
        c.rot += c.spin * dt;
        if (c.y < 0.012) {
          c.y = 0.012;
          c.vy = Math.abs(c.vy) > 0.6 ? -c.vy * 0.35 : 0;
          c.vx *= 0.5;
          c.vz *= 0.5;
          c.spin *= 0.5;
        }
      }
      tmpE.set(Math.PI / 2, c.rot, 0);
      tmpQ.setFromEuler(tmpE);
      tmpS.setScalar(c.shell ? 2.2 : 1.1);
      tmpP.set(c.x, c.y, c.z);
      tmpM.compose(tmpP, tmpQ, tmpS);
      if (c.shell) shells.setMatrixAt(ns++, tmpM);
      else brass.setMatrixAt(nb++, tmpM);
    }
    brass.count = nb;
    shells.count = ns;
    brass.instanceMatrix.needsUpdate = true;
    shells.instanceMatrix.needsUpdate = true;
  }

  function mountHud(): void {
    if (hud || !ctx.uiRoot) return;
    const host = ctx.uiRoot.querySelector('.bh-hud') as HTMLElement | null;
    if (host) hud = new WeaponHud(host);
  }

  function say(speaker: string, text: string, seconds = 3.5): void {
    ctx.services.tryGet('ui')?.say(speaker, text, seconds);
  }

  return {
    id: 'weapon-view',
    order: 713,
    init(c) {
      ctx = c;
      c.scene.add(root);
      c.hooks.register('weaponLineup', (x, z = 0) => {
        for (const g of lineup) {
          g.getObjectByName('glint')?.removeFromParent();
          disposeWeaponMesh(g);
        }
        lineup.length = 0;
        if (x === null) return 0;
        LINEUP.forEach((w, i) => {
          const g = buildPickup(w, false);
          g.position.set(x + i * 1.15, 0, z);
          lineup.push(g);
        });
        return lineup.length;
      });
      mats = acquireWeaponMaterials();
      brass = new THREE.InstancedMesh(casingGeometry(), mats.brass!, CASING_POOL);
      shells = new THREE.InstancedMesh(casingGeometry(), mats.shell!, CASING_POOL);
      brass.count = 0;
      shells.count = 0;
      brass.frustumCulled = false;
      shells.frustumCulled = false;
      root.add(brass, shells);
      offs.push(
        c.events.on('weaponFired', (e) => {
          if (pending.length < 8) pending.push({ kind: 'player', from: e.from, pellets: e.pellets, weapon: e.weapon });
          // Heads turn to the bang (character views, presentation only).
          noteAttention(e.from.x, e.from.z, 1, c.state.time + 3);
        }),
        c.events.on('policeShot', (e) => {
          if (pending.length < 8) pending.push({ kind: 'police', from: e.from, pellets: [{ to: e.to, hit: e.hit ? 'target' : 'none', normal: { x: 0, z: 0 } }], weapon: 'revolver' });
          noteAttention(e.from.x, e.from.z, 0.8, c.state.time + 2.5);
          if (e.hit && (!e.target || e.target === 'player')) noteHit('player', 0, e.to.x, e.to.z, e.from.x, e.from.z, 0.8, c.state.time);
        }),
        c.events.on('weaponHit', (e) => {
          // Flinch or stagger on the figure that was hit; the blow comes from the local player.
          if (e.target === 'vehicle') return;
          hitMark = { x: e.pos.x, z: e.pos.z, kill: !!e.killed || !!e.knocked };
          const from = c.services.tryGet('player')?.pos;
          const strength = e.knocked || e.killed ? 1 : WEAPONS[e.weapon].firearm ? 0.75 : 0.45;
          noteHit(e.target === 'player' ? 'remote' : e.target, e.id, e.pos.x, e.pos.z, from?.x ?? e.pos.x, from?.z ?? e.pos.z - 1, strength, c.state.time);
        }),
        c.events.on('weaponReloaded', (e) => reloadDrops.push({ weapon: e.weapon, pos: e.pos })),
        c.events.on('netShot', (e) => {
          const weapon = weaponFromWire(e.weapon);
          if (remoteShots.length < 16 && WEAPONS[weapon].firearm) remoteShots.push({ eid: e.eid, weapon, from: { x: e.x, z: e.z }, heading: e.h, at: e.at, seed: e.seed });
          if (WEAPONS[weapon].firearm) noteAttention(e.x, e.z, 1, c.state.time + 3);
        }),
        c.events.on('armedResponse', (e) => {
          const ui = c.services.tryGet('ui');
          if (e.stage === 'authorised') {
            ui?.toast('Armed police authorised. They will shoot your tyres out.', 'warn', 5);
            say('Radio', 'All units, armed response is authorised. Take his tyres, don’t take chances.', 4.5);
          } else {
            ui?.toast('Armed police may open fire. Break line of sight.', 'bad', 5);
            say('Radio', 'Armed units, you are clear to fire. Put him down.', 4.5);
          }
        }),
        c.events.on('policeAim', (e) => {
          const now = c.state.time;
          if (now - shoutAt < 7) return;
          shoutAt = now;
          say('Armed officer', e.from === 'car' ? 'ARMED POLICE! Pull over, now!' : 'ARMED POLICE! Stand still, drop it!', 2.5);
        }),
      );
    },
    start() {
      weapons = ctx.services.tryGet('weapons');
      rig = ctx.services.tryGet('playerRig');
      // Sid himself is indoors now, behind his counter (src/weapons/backRoom).
    },
    update(dt) {
      const time = ctx.state.time;
      weapons ??= ctx.services.tryGet('weapons');
      rig ??= ctx.services.tryGet('playerRig');
      mountHud();
      const player = ctx.services.tryGet('player');
      const onFoot = !!player && player.mode !== 'driving' && player.mode !== 'riding';
      if (weapons) ensureHand(weapons.current);
      syncRemoteGuns();
      playRemoteShots();

      for (const s of pending) drawShot(s);
      pending.length = 0;
      for (const d of reloadDrops) dropCasings(d.weapon, d.pos);
      reloadDrops.length = 0;

      if (flashLife > 0) {
        flashLife -= dt;
        flashMat.opacity = Math.max(0, flashLife / FLASH_LIFE);
        if (flashLife <= 0) flash.visible = false;
      }
      for (const t of tracers) {
        if (t.life <= 0) continue;
        t.life -= dt;
        if (t.life <= 0) t.mesh.visible = false;
      }
      updateCasings(dt);
      syncPickups(time);
      updateCopGuns();

      if (hud && weapons) {
        let lock: { x: number; y: number; melee: boolean; key: string } | null = null;
        const l = weapons.lock;
        if (l && ctx.render) {
          const v = tmpV.set(l.x, 2.1, l.z).project(ctx.camera);
          lock = { x: ((v.x + 1) / 2) * ctx.render.canvas.clientWidth, y: ((1 - v.y) / 2) * ctx.render.canvas.clientHeight, melee: weapons.def.firearm === null, key: `${l.kind}:${l.id}` };
        }
        const pad = ctx.input.lastDevice === 'gamepad';
        hud.update(
          {
            def: weapons.def,
            // Bare fists and nothing else carried: keep the corner clear until you square up.
            visible: onFoot && ctx.state.mode !== 'title' && (weapons.current !== 'fists' || weapons.owned().length > 1 || weapons.raised > 0),
            loaded: weapons.loaded,
            reserve: weapons.reserve,
            reload: weapons.reloadProgress,
            carried: weapons.owned().length,
            nextKey: pad ? 'D-pad right' : keyLabel(ctx.input.bindings.keys.weaponNext[0] ?? 'Digit1'),
            lock,
            warn: time < warnUntil && onFoot,
            hit: markHit(),
          },
          dt,
        );
      }
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
      hud?.dispose();
      hud = null;
      for (const g of inHand.values()) disposeWeaponMesh(g);
      inHand.clear();
      for (const g of pickupMeshes.values()) {
        g.getObjectByName('glint')?.removeFromParent();
        disposeWeaponMesh(g);
      }
      pickupMeshes.clear();
      for (const g of lineup) {
        g.getObjectByName('glint')?.removeFromParent();
        disposeWeaponMesh(g);
      }
      lineup.length = 0;
      for (const g of copGuns) disposeWeaponMesh(g);
      for (const [eid, g] of [...remoteGuns]) dropRemoteGun(eid, g);
      for (const l of aimLines) l.geometry.dispose();
      brass?.geometry.dispose();
      shells?.geometry.dispose();
      flashGeo.dispose();
      tracerGeo.dispose();
      glintGeo.dispose();
      for (const m of [flashMat, tracerMat, glintMat, aimMat]) m.dispose();
      releaseWeaponMaterials();
      root.removeFromParent();
    },
  };
}
